import { useCallback, useContext, useState } from 'react'
import { AffiliateDto } from 'api/Affiliate/AffiliateDto'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { Modal } from 'components/CustomModal/modal'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { ClientDetailsWorldCheckHistory } from 'routes/Records/Clients/Details/ClientWorldCheck/ClientDetailsWorldCheckHistory'
import { ClientDetailsWorldCheckModal } from 'routes/Records/Clients/Details/ClientWorldCheck/ClientDetailsWorldCheckModal'
import { AffiliateApiClient } from 'utils/api/affiliateApi'
import { useApiClient } from 'utils/api/ApiClient'
import { TicketsApiClient } from 'utils/api/ticketsApi'
import { WorldCheckApiClient } from 'utils/api/worldCheckApi'
import { AuthSessionContext } from 'utils/AuthContext'

import { AffiliateDetailsForm, AffiliateUpdateDto } from './AffiliateDetailsForm'

export interface AffiliateDetailsPageProps {
  forceRefresh(): void
  data: AffiliateDto
  permissions: PermissionDto[]
  onSubmit(id: string, data: AffiliateUpdateDto): void
}

export function AffiliateDetailsPage({ data, permissions, onSubmit }: AffiliateDetailsPageProps) {
  const [auth] = useContext(AuthSessionContext)

  const worldCheckApiClient = useApiClient(WorldCheckApiClient)
  const ticketsApiClient = useApiClient(TicketsApiClient)
  const setToast = useContext(ToastContext)

  const affiliateApi = useApiClient(AffiliateApiClient)

  const individualWorldCheckScreeningTypeId = 1

  const [worldCheckModal, setWorldCheckModal] = useState<{
    visible: boolean
    enableOngoingScreening: boolean
  }>({
    visible: false,
    enableOngoingScreening: false,
  })

  const [worldCheckClientDetailsModal, setWorldCheckClientDetailsModal] = useState<{
    visible: boolean
    caseId: string | undefined
  }>({
    visible: false,
    caseId: undefined,
  })

  const [worldCheckHistoryModal, setWorldCheckHistoryModal] = useState<boolean>(false)

  const dateFormatType = auth?.user.dateFormatType.name

  const handleWorldCheckResolve = () => {
    setToast(successToast('WorldCheck status updated.'))
  }

  const handleWorldCheckFailure = () => {
    setToast(successToast('WorldCheck failed.'))
  }

  const handleWorldCheckNoRecordsFoundResolve = async () => {
    setWorldCheckModal({ visible: false, enableOngoingScreening: false })
  }

  const handleWorldCheckScreeningModelOpen = (caseId: string) => {
    setWorldCheckClientDetailsModal({ visible: true, caseId })
  }

  const handleWorldCheckScreeningModelClose = () => {
    setWorldCheckClientDetailsModal({ visible: false, caseId: undefined })
  }

  const onWorldCheckNoRecordsFoundResolve = async () => {
    setWorldCheckModal({ visible: false, enableOngoingScreening: false })
  }

  const { id: clientId } = data
  const fetchData = useCallback(async () => {
    if (clientId) {
      return affiliateApi.affiliateWorldCheck(clientId)
    }
  }, [clientId]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {worldCheckClientDetailsModal.visible && (
        <Modal
          draggable
          noDragZone='table'
          visible={worldCheckClientDetailsModal.visible}
          width={1400}
          overflow='auto'
          onClose={handleWorldCheckScreeningModelClose}
        >
          <ClientDetailsWorldCheckModal
            clientId={data.id}
            typeId={individualWorldCheckScreeningTypeId}
            worldCheckApiClient={worldCheckApiClient}
            ticketsApiClient={ticketsApiClient}
            enableOngoingScreening={worldCheckModal.enableOngoingScreening}
            onResolve={handleWorldCheckResolve}
            onFailure={handleWorldCheckFailure}
            onWorldCheckNoRecordsFoundResolve={onWorldCheckNoRecordsFoundResolve}
            affiliateWorldCheck={fetchData}
          />
        </Modal>
      )}
      {worldCheckHistoryModal && (
        <Modal
          draggable
          noDragZone='table'
          visible={worldCheckHistoryModal}
          width={1200}
          overflow='auto'
          onClose={() => setWorldCheckHistoryModal(false)}
        >
          <ClientDetailsWorldCheckHistory
            clientId={data.id}
            typeId={individualWorldCheckScreeningTypeId}
            worldCheckApiClient={worldCheckApiClient}
            onWorldCheckScreeningModelOpen={handleWorldCheckScreeningModelOpen}
          />
        </Modal>
      )}
      {worldCheckModal.visible && (
        <Modal
          visible={worldCheckModal.visible}
          width={1400}
          overflow='auto'
          onClose={() => setWorldCheckModal({ visible: false, enableOngoingScreening: false })}
          draggable
        >
          <ClientDetailsWorldCheckModal
            clientId={data.id}
            typeId={individualWorldCheckScreeningTypeId}
            worldCheckApiClient={worldCheckApiClient}
            ticketsApiClient={ticketsApiClient}
            enableOngoingScreening={worldCheckModal.enableOngoingScreening}
            onResolve={handleWorldCheckResolve}
            onFailure={handleWorldCheckFailure}
            onWorldCheckNoRecordsFoundResolve={handleWorldCheckNoRecordsFoundResolve}
            affiliateWorldCheck={fetchData}
          />
        </Modal>
      )}
      <AffiliateDetailsForm
        data={data}
        permissions={permissions}
        onSubmit={onSubmit}
        dateFormatType={dateFormatType}
        onSetWorldCheckModal={setWorldCheckModal}
        onSetWorldCheckHistoryModal={setWorldCheckHistoryModal}
      />
    </>
  )
}
